import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Calendar, CheckCircle2, Clock, AlertCircle, Search, X,
  ChevronLeft, ChevronRight, Download,
  RefreshCw, ArrowUpDown, ArrowUp, ArrowDown,
  Info, HeartPulse
} from 'lucide-react';
import { usePatientMilestones, fetchMilestoneFilterOptions, fetchMilestonesExport } from '../hooks/usePatientMilestones';

// ─── Constants ────────────────────────────────────────────────────────────────

const MILESTONE_TABS = [
  { key: 'all', label: 'Overview' },
  { key: 'upcoming', label: 'Upcoming', isStatus: true },
  { key: '90-day', label: '90-Day' },
  { key: '180-day', label: '180-Day' },
  { key: 'm-9', label: 'M-9' },
  { key: '1-year', label: '1-Year' },
  { key: 'm-15', label: 'M-15' },
  { key: 'm-18', label: 'M-18' },
  { key: 'm-21', label: 'M-21' },
  { key: 'm-24', label: 'M-24' },
  { key: 'm-30', label: 'M-30' },
  { key: 'm-42', label: 'M-42' },
  { key: 'm-48', label: 'M-48' },
];

const STATUS_COLORS = {
  upcoming:         { bg: 'bg-blue-50',    text: 'text-blue-700',    dot: 'bg-blue-500' },
  pending:          { bg: 'bg-orange-50',  text: 'text-orange-700',  dot: 'bg-orange-500' },
  completed:        { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  deceased:         { bg: 'bg-gray-100',   text: 'text-gray-600',    dot: 'bg-gray-500' },
  lost_to_followup: { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500' },
};

const STATUS_LABELS = {
  upcoming:         'Upcoming',
  pending:          'Pending',
  completed:        'Completed',
  deceased:         'Deceased',
  lost_to_followup: 'Lost to Follow-Up',
};

const CR_COLORS = {
  cr_confirmed:  { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  cr_subsequent: { bg: 'bg-teal-50',    text: 'text-teal-700',    dot: 'bg-teal-500' },
  not_in_cr:     { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500' },
  deceased:      { bg: 'bg-gray-100',   text: 'text-gray-600',    dot: 'bg-gray-500' },
  pending:       { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500' },
};

const CR_LABELS = {
  cr_confirmed:  'CR Confirmed',
  cr_subsequent: 'CR (subseq. therapy)',
  not_in_cr:     'Not in CR',
  deceased:      'Deceased',
  pending:       'Pending',
};

// Filter dropdown option lists (label shown, value sent to the API)
const MILESTONE_STATUS_OPTIONS = [
  { label: 'Upcoming', value: 'upcoming' },
  { label: 'Pending', value: 'pending' },
  { label: 'Completed', value: 'completed' },
  { label: 'Deceased', value: 'deceased' },
  { label: 'Lost to Follow-Up', value: 'lost_to_followup' },
];

const CR_STATUS_OPTIONS = [
  { label: 'Sustained CR', value: 'sustained' },
  { label: 'CR Confirmed', value: 'cr_confirmed' },
  { label: 'CR (subseq. therapy)', value: 'cr_subsequent' },
  { label: 'Not in CR', value: 'not_in_cr' },
  { label: 'Pending', value: 'pending' },
  // 'Deceased' is intentionally not a CR-status filter: deceased patients are
  // filtered via the Milestone Status dropdown, and the CR column shows a dash.
];

// CSV cells beginning with these are prefixed with an apostrophe on export so
// spreadsheet software cannot interpret them as formulas.
const CSV_INJECTION_CHARS = ['=', '+', '-', '@', '\t', '\r'];

function csvCell(value) {
  let s = value == null ? '' : String(value);
  if (s.length && CSV_INJECTION_CHARS.includes(s[0])) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}

// ─── Badges ───────────────────────────────────────────────────────────────────

const MilestoneStatusBadge = ({ status, context }) => {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <div className="flex flex-col items-center">
      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${colors.bg} ${colors.text}`}>
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colors.dot}`} />
        {STATUS_LABELS[status] || status}
      </div>
      {context && (
        <span className={`text-[10px] mt-0.5 whitespace-nowrap ${colors.text} opacity-75`}>{context}</span>
      )}
    </div>
  );
};

const CRStatusBadge = ({ status }) => {
  // For deceased patients the Milestone Status column already shows "Deceased";
  // repeating it here adds no information, so show a neutral dash instead.
  if (status === 'deceased') {
    return (
      <div className="flex justify-center">
        <span className="text-gray-300" title="Not applicable - patient deceased">-</span>
      </div>
    );
  }
  const colors = CR_COLORS[status] || CR_COLORS.pending;
  return (
    <div className="flex justify-center">
      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${colors.bg} ${colors.text}`}>
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colors.dot}`} />
        {CR_LABELS[status] || status}
      </div>
    </div>
  );
};

const MilestoneBadge = ({ milestone }) => {
  const colorMap = {
    '90-Day': 'bg-sky-100 text-sky-700',
    '180-Day': 'bg-violet-100 text-violet-700',
    'M-9': 'bg-blue-100 text-blue-700',
    '1-Year': 'bg-indigo-100 text-indigo-700',
    'M-15': 'bg-teal-100 text-teal-700',
    'M-18': 'bg-cyan-100 text-cyan-700',
    'M-21': 'bg-purple-100 text-purple-700',
    'M-24': 'bg-fuchsia-100 text-fuchsia-700',
    'M-30': 'bg-rose-100 text-rose-700',
    'M-42': 'bg-amber-100 text-amber-700',
    'M-48': 'bg-lime-100 text-lime-700',
  };
  const colors = colorMap[milestone] || 'bg-gray-100 text-gray-700';
  return <span className={`px-2 py-0.5 rounded-md text-xs font-semibold whitespace-nowrap ${colors}`}>{milestone}</span>;
};

// ─── Summary Card ─────────────────────────────────────────────────────────────
//
// Headline = patients in sustained complete remission at this milestone.
// Total cohort size sits beside the heading for context (client feedback:
// replace the repeated "408 Total Patients" with the CR count).

const StatCell = ({ value, label, valueClass, tooltip }) => (
  <div className="text-center" title={tooltip}>
    <p className={`text-base font-bold ${valueClass}`}>{value}</p>
    <p className="text-[10px] text-gray-500 font-medium leading-tight">{label}</p>
  </div>
);

const SummaryCard = ({ title, icon: Icon, data, accentColor, borderColor, periodLabel }) => {
  if (!data) return null;

  const cr        = data.cr_raw != null ? data.cr_raw : (data.cr_sustained || 0);
  const notInCr   = data.not_in_cr_total != null ? data.not_in_cr_total : (data.not_in_cr || 0);
  const awaiting  = data.awaiting || 0;
  const deceased  = data.deceased || 0;
  const ltf       = data.lost_to_followup || 0;
  const assessed  = cr + notInCr;
  const rate      = assessed > 0 ? Math.round((cr / assessed) * 100) : null;

  return (
    <div className={`bg-white rounded-xl border-2 ${borderColor} shadow-sm p-5 flex-1 min-w-[300px]`}>
      {/* Heading row */}
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2.5 rounded-lg ${accentColor}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      </div>

      {/* Headline: patients in complete remission at this milestone */}
      <div className="mb-3">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-emerald-600">{cr}</span>
          <span className="text-xs font-medium text-gray-600">patients in complete remission</span>
        </div>
        {/* <p className="text-[11px] text-gray-500 mt-0.5">
          In complete remission {periodLabel ? `at ${periodLabel}` : ''}
        </p> */}
        {rate != null && (
          <p className="text-[11px] text-gray-400 mt-1">
            <span className="font-semibold text-emerald-600">{rate}% remission rate</span>
            {' '}&middot; {cr} of {assessed} assessed
          </p>
        )}
      </div>

      {/* Where everyone else is */}
      <div className="grid grid-cols-4 gap-2 pt-3 border-t border-gray-100">
        <StatCell value={notInCr} label="Not in remission" valueClass="text-red-600"
          tooltip="Assessed, but not in complete remission (partial response, stable or progressive disease)" />
        <StatCell value={awaiting} label="Awaiting result" valueClass="text-amber-600"
          tooltip="Still in follow-up, but no assessment recorded for this milestone yet" />
        <StatCell value={deceased} label="Deceased" valueClass="text-gray-500"
          tooltip="Patients who died during this interval (since the previous milestone). Counted once, on this card only." />
        <StatCell value={ltf} label="Lost to follow-up" valueClass="text-amber-500"
          tooltip="Patients whose follow-up stopped during this interval. Counted once, on this card only." />
      </div>
    </div>
  );
};

// ─── Filter Dropdown ──────────────────────────────────────────────────────────
// options: array of strings OR { label, value } objects.

const FilterDropdown = ({ label, value, options, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  const normOptions = useMemo(
    () => options.map(o => (typeof o === 'string' ? { label: o, value: o } : o)),
    [options]
  );

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    if (!search) return normOptions;
    return normOptions.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  }, [normOptions, search]);

  const selectedLabel = useMemo(() => {
    const hit = normOptions.find(o => o.value === value);
    return hit ? hit.label : '';
  }, [normOptions, value]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
          value ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
        }`}
      >
        <span className="truncate max-w-[140px]">{selectedLabel || label}</span>
        {value ? (
          <X className="w-3 h-3 cursor-pointer" onClick={(e) => { e.stopPropagation(); onChange(''); }} />
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-40" />
        )}
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-60 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {normOptions.length > 5 && (
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={placeholder || `Search ${label.toLowerCase()}...`}
                  className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded focus:ring-emerald-500 focus:border-emerald-500"
                  autoFocus
                />
              </div>
            </div>
          )}
          <div className="max-h-52 overflow-y-auto">
            <button
              onClick={() => { onChange(''); setIsOpen(false); setSearch(''); }}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${!value ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600'}`}
            >
              All
            </button>
            {filtered.map(opt => (
              <button
                key={opt.value}
                onClick={() => { onChange(opt.value); setIsOpen(false); setSearch(''); }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${value === opt.value ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Pagination ───────────────────────────────────────────────────────────────

const Pagination = ({ page, totalPages, total, pageSize, onPageChange }) => {
  if (totalPages <= 1) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
  }

  return (
    <div className="flex items-center justify-between px-1 py-3">
      <span className="text-xs text-gray-500">Showing {start} to {end} of {total} records</span>
      <div className="flex items-center gap-1">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1}
          className="p-1.5 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        {pages.map((p, i) => (
          p === '...' ? (
            <span key={`dots-${i}`} className="px-2 text-xs text-gray-400">…</span>
          ) : (
            <button key={p} onClick={() => onPageChange(p)}
              className={`w-7 h-7 rounded-md text-xs font-medium transition-colors ${p === page ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}>
              {p}
            </button>
          )
        ))}
        <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}
          className="p-1.5 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// ─── Sort Header ──────────────────────────────────────────────────────────────

const SortHeader = ({ label, sortKey, currentSort, currentOrder, onSort, center = false }) => {
  const isActive = currentSort === sortKey;
  return (
    <button onClick={() => onSort(sortKey)}
      className={`flex items-center gap-1 text-xs font-semibold text-gray-600 uppercase tracking-wider hover:text-gray-900 transition-colors ${center ? 'justify-center mx-auto' : ''}`}>
      {label}
      {isActive
        ? (currentOrder === 'asc' ? <ArrowUp className="w-3 h-3 text-emerald-600" /> : <ArrowDown className="w-3 h-3 text-emerald-600" />)
        : <ArrowUpDown className="w-3 h-3 text-gray-300" />}
    </button>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const PatientMilestones = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [filterOptions, setFilterOptions] = useState({ hospitals: [], physicians: [], indications: [], rr_categories: [], zones: [] });
  const [exporting, setExporting] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  const getTabParams = (tab) => {
    // Overview = one row per patient, showing their current milestone state.
    if (tab === 'all') return { milestone_type: 'current', milestone_status: 'all' };
    if (tab === 'upcoming') return { milestone_type: 'all', milestone_status: 'upcoming' };
    return { milestone_type: tab, milestone_status: 'all' };
  };

  const { data, loading, error, params, updateParams, setPage, resetFilters, refresh } =
    usePatientMilestones(getTabParams(activeTab));

  useEffect(() => {
    fetchMilestoneFilterOptions().then(setFilterOptions).catch(() => {});
  }, []);

  // Debounce the free-text search so we don't fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => {
      if (searchInput !== params.search) updateParams({ search: searchInput });
    }, 350);
    return () => clearTimeout(id);
  }, [searchInput, params.search, updateParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    updateParams({ ...getTabParams(tab), page: 1 });
  };

  const handleSort = (sortKey) => {
    const newOrder = params.sort_by === sortKey && params.sort_order === 'asc' ? 'desc' : 'asc';
    updateParams({ sort_by: sortKey, sort_order: newOrder });
  };

  const handleClearAll = useCallback(() => {
    setActiveTab('all');       // keep the tab highlight in sync with the reset
    setSearchInput('');
    resetFilters();
    // resetFilters clears to milestone_type 'all'; Overview is the 'current'
    // (one-row-per-patient) view, so restore that.
    updateParams(getTabParams('all'));
  }, [resetFilters, updateParams]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await fetchMilestonesExport({
        milestone_type: params.milestone_type,
        milestone_status: params.milestone_status,
        cr_status: params.cr_status,
        hospital: params.hospital,
        physician: params.physician,
        indication: params.indication,
        rr_category: params.rr_category,
        zone: params.zone,
        search: params.search,
      });

      const rowsData = result.patients || [];
      const headers = [
        'Patient ID', 'Hospital', 'Physician', 'Indication', 'R/R Category', 'Zone',
        'Infusion Date', 'Milestone', 'Milestone Date', 'Milestone Status', 'Milestone Context',
        'CR Status', 'Sustained CR', 'Response Value', 'Last Follow-Up',
      ];
      const rows = rowsData.map(p => [
        p.patient_id, p.hospital, p.physician, p.indication, p.rr_category, p.zone,
        p.infusion_date, p.milestone, p.milestone_date,
        STATUS_LABELS[p.milestone_status] || p.milestone_status,
        p.milestone_context || '',
        CR_LABELS[p.cr_status] || p.cr_status,
        p.cr_sustained ? 'Yes' : 'No',
        p.response_value || '',
        p.last_follow_up || '',
      ]);

      const csvContent = [headers.map(csvCell).join(','), ...rows.map(r => r.map(csvCell).join(','))].join('\r\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `patient_milestones_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary || {};
  const patients = data?.patients || [];
  const pagination = data?.pagination || { page: 1, page_size: 10, total: 0, total_pages: 0 };
  const hasActiveFilters = params.hospital || params.physician || params.indication ||
    params.rr_category || params.zone || params.search ||
    (params.cr_status && params.cr_status !== 'all');

  return (
    <div className="max-w-[1920px] mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Patient Milestones</h1>
        <p className="text-sm text-gray-500 mt-1">
          Track sustained complete remission across follow-up milestones from infusion, alongside milestone completion status.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="flex flex-wrap gap-4 mb-6">
        <SummaryCard title="90-Day Milestone" periodLabel="90 days" icon={Calendar} data={summary['90-day']} accentColor="bg-sky-500" borderColor="border-sky-200" />
        <SummaryCard title="180-Day Milestone" periodLabel="180 days" icon={Clock} data={summary['180-day']} accentColor="bg-violet-500" borderColor="border-violet-200" />
        <SummaryCard title="1-Year Milestone" periodLabel="1 year" icon={CheckCircle2} data={summary['1-year']} accentColor="bg-indigo-500" borderColor="border-indigo-200" />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        {/* Tabs */}
        <div className="border-b border-gray-200 px-4 pt-4 overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {MILESTONE_TABS.map(tab => (
              <button key={tab.key} onClick={() => handleTabChange(tab.key)}
                className={`px-3 py-2 text-xs font-medium rounded-t-lg border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-emerald-600 text-emerald-700 bg-emerald-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search patient ID, hospital..."
              className="pl-8 pr-3 py-1.5 w-56 text-xs border border-gray-200 rounded-lg focus:ring-emerald-500 focus:border-emerald-500"
            />
            {searchInput && (
              <button onClick={() => setSearchInput('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                <X className="w-3 h-3 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          {activeTab !== 'upcoming' && (
            <FilterDropdown
              label="Milestone Status"
              value={params.milestone_status !== 'all' ? params.milestone_status : ''}
              options={MILESTONE_STATUS_OPTIONS}
              onChange={(v) => updateParams({ milestone_status: v || 'all' })}
            />
          )}

          <FilterDropdown
            label="CR Status"
            value={params.cr_status !== 'all' ? params.cr_status : ''}
            options={CR_STATUS_OPTIONS}
            onChange={(v) => updateParams({ cr_status: v || 'all' })}
          />

          <FilterDropdown label="Hospital" value={params.hospital} options={filterOptions.hospitals}
            onChange={(v) => updateParams({ hospital: v })} placeholder="Search hospitals..." />

          <FilterDropdown label="Physician" value={params.physician} options={filterOptions.physicians}
            onChange={(v) => updateParams({ physician: v })} placeholder="Search physicians..." />

          <FilterDropdown
            label="Indication"
            value={params.indication}
            options={[{ label: 'Lymphoma (NHL)', value: 'nhl' }, { label: 'Leukemia (B-ALL)', value: 'all' }]}
            onChange={(v) => updateParams({ indication: v })}
          />

          {filterOptions.zones && filterOptions.zones.length > 0 && (
            <FilterDropdown label="Zone" value={params.zone} options={filterOptions.zones}
              onChange={(v) => updateParams({ zone: v })} />
          )}

          <div className="flex-1" />

          {hasActiveFilters && (
            <button onClick={handleClearAll}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-3 h-3" /> Clear All
            </button>
          )}

          <button onClick={handleExport} disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50">
            <Download className="w-3.5 h-3.5" />
            {exporting ? 'Exporting...' : 'Export'}
          </button>
        </div>

        {/* Context caption */}
        {activeTab === 'all' && !loading && !error && patients.length > 0 && (
          <div className="px-4 pt-3 mb-2">
            <p className="text-xs text-gray-400 flex items-center gap-1.5">
              <Info className="w-3 h-3 flex-shrink-0" />
              One row per patient showing their current status. Use the milestone tabs to see a specific timepoint.
            </p>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="w-8 h-8 border-[3px] border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-xs text-gray-500">Loading milestones...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
                <p className="text-sm text-red-600 font-medium">{error}</p>
                <button onClick={refresh} className="mt-3 text-xs text-emerald-600 hover:underline flex items-center gap-1 mx-auto">
                  <RefreshCw className="w-3 h-3" /> Retry
                </button>
              </div>
            </div>
          ) : patients.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No milestone data found for the selected filters.</p>
              </div>
            </div>
          ) : activeTab === 'all' ? (
            /* ── OVERVIEW TABLE — one row per patient, no milestone math ── */
            <table className="w-full table-fixed">
              <colgroup>
                <col className="w-[18%]" />{/* Patient ID */}
                <col className="w-[16%]" />{/* Hospital */}
                <col className="w-[13%]" />{/* Physician */}
                <col className="w-[11%]" />{/* R/R Category */}
                <col className="w-[9%]"  />{/* Infusion Date */}
                <col className="w-[13%]" />{/* Current Status */}
                <col className="w-[9%]" />{/* CR Status */}
                <col className="w-[11%]"  />{/* Last F/U */}
              </colgroup>
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-3 text-center align-middle"><SortHeader label="Patient ID" sortKey="patient_id" currentSort={params.sort_by} currentOrder={params.sort_order} onSort={handleSort} center /></th>
                  <th className="px-3 py-3 text-center align-middle"><SortHeader label="Hospital" sortKey="hospital" currentSort={params.sort_by} currentOrder={params.sort_order} onSort={handleSort} center /></th>
                  <th className="px-3 py-3 text-center align-middle text-xs font-semibold text-gray-600 uppercase tracking-wider">Physician</th>
                  <th className="px-3 py-3 text-center align-middle text-xs font-semibold text-gray-600 uppercase tracking-wider">R/R Category</th>
                  <th className="px-3 py-3 text-center align-middle text-xs font-semibold text-gray-600 uppercase tracking-wider">Infusion</th>
                  <th className="px-3 py-3 text-center align-middle"><SortHeader label="Status" sortKey="milestone_status" currentSort={params.sort_by} currentOrder={params.sort_order} onSort={handleSort} center /></th>
                  <th className="px-3 py-3 text-center align-middle"><SortHeader label="CR Status" sortKey="cr_status" currentSort={params.sort_by} currentOrder={params.sort_order} onSort={handleSort} center /></th>
                  <th className="px-3 py-3 text-center align-middle text-xs font-semibold text-gray-600 uppercase tracking-wider">Last F/U</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {patients.map((patient, idx) => (
                  <tr key={`${patient.patient_id}-overview-${idx}`} className="hover:bg-gray-50 transition-colors">
                    {/* Full Patient ID — no truncate */}
                    <td className="px-3 py-3 align-middle text-center text-xs font-medium text-gray-900 break-all">{patient.patient_id}</td>
                    <td className="px-3 py-3 align-middle text-center text-xs text-gray-600 truncate" title={patient.hospital}>{patient.hospital}</td>
                    <td className="px-3 py-3 align-middle text-center text-xs text-gray-600 truncate" title={patient.physician}>{patient.physician}</td>
                    <td className="px-3 py-3 align-middle text-center text-xs text-gray-600">{patient.rr_category}</td>
                    <td className="px-3 py-3 align-middle text-center text-xs text-gray-500 whitespace-nowrap">{patient.infusion_date}</td>
                    <td className="px-3 py-3 align-middle">
                      <MilestoneStatusBadge
                        status={patient.milestone_status}
                        context={patient.milestone_status === 'deceased' ? patient.milestone_context : null}
                      />
                    </td>
                    <td className="px-3 py-3 align-middle"><CRStatusBadge status={patient.cr_status} /></td>
                    <td className="px-3 py-3 align-middle text-center text-xs text-gray-500 whitespace-nowrap">
                      {patient.milestone_status === 'deceased'
                        ? (patient.death_date || patient.last_follow_up || '-')
                        : (patient.last_follow_up || '-')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            /* ── MILESTONE TAB TABLE — full columns ── */
            <table className="w-full table-fixed">
              <colgroup>
                <col className="w-[18%]" />{/* Patient ID — full, no truncate */}
                <col className="w-[13%]" />{/* Hospital */}
                <col className="w-[10%]" />{/* Physician */}
                <col className="w-[9%]"  />{/* R/R Category */}
                <col className="w-[8%]"  />{/* Infusion Date */}
                <col className="w-[8%]"  />{/* Milestone */}
                <col className="w-[7%]"  />{/* Milestone Date */}
                <col className="w-[10%]" />{/* Milestone Status */}
                <col className="w-[9%]"  />{/* CR Status */}
                <col className="w-[8%]"  />{/* Last Follow-Up */}
              </colgroup>
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-3 text-center align-middle"><SortHeader label="Patient ID" sortKey="patient_id" currentSort={params.sort_by} currentOrder={params.sort_order} onSort={handleSort} center /></th>
                  <th className="px-3 py-3 text-center align-middle"><SortHeader label="Hospital" sortKey="hospital" currentSort={params.sort_by} currentOrder={params.sort_order} onSort={handleSort} center /></th>
                  <th className="px-3 py-3 text-center align-middle text-xs font-semibold text-gray-600 uppercase tracking-wider">Physician</th>
                  <th className="px-3 py-3 text-center align-middle text-xs font-semibold text-gray-600 uppercase tracking-wider">R/R Category</th>
                  <th className="px-3 py-3 text-center align-middle text-xs font-semibold text-gray-600 uppercase tracking-wider">Infusion</th>
                  <th className="px-3 py-3 text-center align-middle text-xs font-semibold text-gray-600 uppercase tracking-wider">Milestone</th>
                  <th className="px-3 py-3 text-center align-middle"><SortHeader label="M. Date" sortKey="milestone_date" currentSort={params.sort_by} currentOrder={params.sort_order} onSort={handleSort} center /></th>
                  <th className="px-3 py-3 text-center align-middle"><SortHeader label="Status" sortKey="milestone_status" currentSort={params.sort_by} currentOrder={params.sort_order} onSort={handleSort} center /></th>
                  <th className="px-3 py-3 text-center align-middle"><SortHeader label="CR Status" sortKey="cr_status" currentSort={params.sort_by} currentOrder={params.sort_order} onSort={handleSort} center /></th>
                  <th className="px-3 py-3 text-center align-middle text-xs font-semibold text-gray-600 uppercase tracking-wider">Last F/U</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {patients.map((patient, idx) => (
                  <tr key={`${patient.patient_id}-${patient.milestone_key}-${idx}`} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-3 align-middle text-center text-xs font-medium text-gray-900 break-all">{patient.patient_id}</td>
                    <td className="px-3 py-3 align-middle text-center text-xs text-gray-600 truncate" title={patient.hospital}>{patient.hospital}</td>
                    <td className="px-3 py-3 align-middle text-center text-xs text-gray-600 truncate" title={patient.physician}>{patient.physician}</td>
                    <td className="px-3 py-3 align-middle text-center text-xs text-gray-600">{patient.rr_category}</td>
                    <td className="px-3 py-3 align-middle text-center text-xs text-gray-500 whitespace-nowrap">{patient.infusion_date}</td>
                    <td className="px-3 py-3 align-middle text-center"><MilestoneBadge milestone={patient.milestone} /></td>
                    <td className="px-3 py-3 align-middle text-center text-xs text-gray-500 whitespace-nowrap">{patient.milestone_date}</td>
                    <td className="px-3 py-3 align-middle"><MilestoneStatusBadge status={patient.milestone_status} context={patient.milestone_context} /></td>
                    <td className="px-3 py-3 align-middle"><CRStatusBadge status={patient.cr_status} /></td>
                    <td className="px-3 py-3 align-middle text-center text-xs text-gray-500 whitespace-nowrap">{patient.last_follow_up || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!loading && patients.length > 0 && (
          <div className="px-4 border-t border-gray-100">
            <Pagination page={pagination.page} totalPages={pagination.total_pages} total={pagination.total} pageSize={pagination.page_size} onPageChange={setPage} />
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-6 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">Milestone Status</h4>
            <div className="space-y-2">
              <LegendRow dot="bg-blue-500" term="Upcoming" desc="Milestone date not yet reached" />
              <LegendRow dot="bg-orange-500" term="Pending" desc="Date reached, assessment/data pending" />
              <LegendRow dot="bg-emerald-500" term="Completed" desc="Date reached and assessment data available" />
              <LegendRow dot="bg-gray-500" term="Deceased" desc="Patient died before this milestone" />
              <LegendRow dot="bg-amber-500" term="Lost to Follow-Up" desc="No further contact before this milestone (last follow-up date was before the milestone was due)" />
            </div>
          </div>
          <div>
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">CR Status</h4>
            <div className="space-y-2">
              <LegendRow dot="bg-emerald-500" term="CR Confirmed" desc="Assessment confirms Complete Remission" />
              <LegendRow dot="bg-teal-500" term="CR (subseq. therapy)" desc="In CR, but achieved on a later line of therapy - not counted as a NexCAR19 remission" />
              <LegendRow dot="bg-red-500" term="Not in CR" desc="Assessed as PR, SD or PD" />
              <LegendRow dot="bg-amber-500" term="Pending" desc="Assessment not yet available" />
            </div>
          </div>
        </div>
        <div className="mt-4 pt-3 border-t border-gray-100 space-y-2">
          <div className="flex items-start gap-2">
            <HeartPulse className="w-3.5 h-3.5 text-emerald-500 mt-[1px] flex-shrink-0" />
            <p className="text-[11px] text-gray-500">
              <b>CR count</b> (the headline card figure) counts patients who were in complete remission at that milestone's assessment. A patient with CR at 180 days is counted even if they had an earlier progression.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-gray-400 mt-[1px] flex-shrink-0" />
            <p className="text-[11px] text-gray-500">
              Milestone dates are calculated from the Date of Infusion. On the summary cards, deceased and lost-to-follow-up patients are counted once - on the card for the interval in which the event occurred - and are not carried onto later cards. Patients who died or were lost to follow-up after the 1-Year milestone are not reflected in the three cards above; they appear on the relevant milestone tabs (M-15, M-18, M-21, etc.).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const LegendRow = ({ dot, term, desc }) => (
  <div className="flex items-center gap-2">
    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1 ${dot}`} />
    <div>
      <span className="text-xs font-medium text-gray-700">{term}</span>
      <span className="text-xs text-gray-500 ml-1">- {desc}</span>
    </div>
  </div>
);

export default PatientMilestones;
